import { Component } from "react";
import { ConnectedComponent } from "react-redux";

import { ApplicationAction } from "state/actions/types/applicationActionsTypes";
import { CheckoutAction } from "state/actions/types/checkoutActionsTypes";
import { StatusReducer } from "state/reducers/status";
import { ApplicationUIReducer } from "state/reducers/ui/applicationUI";
import { ProductsReducer } from "state/reducers/products";
import { CheckoutReducer } from "state/reducers/checkout";
import { MiniCartUIReducer } from "state/reducers/ui/miniCartUI";

export interface ViewProps {
  model: any;
}

export class View<Props extends ViewProps, State = {}> extends Component<
  Props,
  State
> {}

export enum Status {
  IDLE = "IDLE",
  PENDING = "PENDING",
  REJECTED = "REJECTED",
  FULFILLED = "FULFILLED"
}

export enum Color {
  "black" = "#000",
  "gray-darker" = "#353535",
  "gray-dark" = "#4a4a4a",
  "gray" = "#979797",
  "gray-light" = "#d8d8d8",
  "gray-lighter" = "#f4f4f5",
  "orange" = "#dd4127",
  "white" = "#ffffff"
}

export interface RouteObject {
  path: string;
  exact: boolean;
  component?: ConnectedComponent<any, any>;
  base?: string;
}

export type Action = ApplicationAction | CheckoutAction;

export enum Environment {
  STAGING = "STAGING",
  PRODUCTION = "PRODUCTION",
  DEVELOPMENT = "DEVELOPMENT"
}

export interface GlobalState {
  status: StatusReducer;
  applicationUI: ApplicationUIReducer;
  products: ProductsReducer;
  session: {
    checkout: CheckoutReducer;
  };
  miniCartUI: MiniCartUIReducer;
}

export type ContentfulContentType = "product" | "global";

export interface GlobalSettings {
  instagramLink: string;
  additionalReadings: Article[];
  termsConditions: string;
  faq: Faq[];
  shopProducts: string[];
}

export interface Faq {
  title: string;
  content: string;
}
export interface Media {
  title: string;
  description: string;
  url: string;
}

export interface Param {
  productHandle: string;
}

export interface Article {
  id: string;
  articleType: ArticleType;
  slug: string;
  thumbnail: Media;
  title: string;
}

export enum ArticleTypes {
  PARALLEL_PRACTICES = "PARALLEL_PRACTICES",
  CREATIVE_CONNECTIONS = "CREATIVE_CONNECTIONS",
  THE_ART_OF = "THE_ART_OF",
  MOMENTS = "MOMENTS",
  MUSINGS = "MUSINGS",
  MUSE_BOARDS = "MUSE_BOARDS",
  MUSTS = "MUSTS"
}

export type ArticleType =
  | ArticleTypes.PARALLEL_PRACTICES
  | ArticleTypes.CREATIVE_CONNECTIONS
  | ArticleTypes.THE_ART_OF
  | ArticleTypes.MOMENTS
  | ArticleTypes.MUSINGS
  | ArticleTypes.MUSE_BOARDS
  | ArticleTypes.MUSTS;

export interface ShopifyProducts {
  [productHandle: string]: ShopifyProduct;
}

export interface ShopifyProduct {
  id: string;
  title: string;
  handle: string;
  variants: ShopifyProductVariant[];
  options: ShopifyProductOption[];
}

export interface ShopifyProductVariant {
  id: string;
  availableForSale: boolean;
  price: {
    amount: number;
  };
  title: string;
  variantOptions: string[];
  product: {
    id: string;
    handle: string;
  };
}

export interface ShopifyProductOption {
  id: string;
  name: string;
  values: string[];
}

export type ContentfulProducts = ContentfulProduct[];

export interface ContentfulProduct {
  title: string;
  productHandle: string;
  care: string;
  fit: string;
  details: string;
  productStickerLabel: string;
  colorVariantsInfo: ColorVariantsInfo[];
  variants: ContentfulVariant[];
  prefix: string;
  moreProducts: string[];
}

export interface ColorVariantsInfo {
  name: string;
  hexColorCode: string;
}

export interface ContentfulVariant {
  images: Media[];
  associatedVariantValues: AssociatedVariantValues;
}

export type AssociatedVariantValues = string[];

export type Products = {
  [productHandle: string]: Product;
};

export interface Product {
  id: string;
  title: string;
  productHandle: string;
  care: string;
  fit: string;
  details: string;
  stickerLabel: string;
  colorVariantsInfo: ColorVariantsInfo[];
  variantOptions: ShopifyProductOption[];
  images: Media[][];
  variants: Variant[];
  prefix: string;
  moreProducts: string[];
  notAvailableOptions: string[][];
}

export interface Variant {
  variantId: string;
  availableForSale: boolean;
  price: {
    amount: number;
  };
  product: {
    id: string;
    handle: string;
  };
  variantOptions: string[];
  title: string;
  imageIndex: number;
}

export enum StickerLabel {
  BACK_IN_STOCK = "BACK_IN_STOCK",
  NEW = "NEW"
}

export enum ProductDetailDescriptionType {
  DETAILS = "details",
  FIT = "fit",
  CARE = "care",
  SHARE = "share"
}

export interface userError {
  field: string;
  message: string;
}

export interface Checkout {
  completedAt: Date | null;
  currencyCode: string;
  id: string;
  note: string;
  subtotalPrice: {
    amount: number;
  };
  totalPrice: {
    amount: number;
  };
  webUrl: string;
  lineItems: LineItems;
  totalLineItemsCount: number;
}

export type LineItems = LineItem[];

export interface LineItem {
  variantId: string;
  quantity: number;
  productId: string;
  productHandle: string;
}

export type UpdatingLineItems = UpdatingLineItem[];

export interface UpdatingLineItem {
  variantId: string;
  quantity: number;
}

export type CartLineItems = CartLineItem[];

export interface CartLineItem {
  prefix: string;
  title: string;
  price: {
    amount: number;
  };
  variantOptions: string[];
  productOptions: ShopifyProductOption[];
  quantity: number;
  image: Media;
  productHandle: string;
  variantId: string;
}
